import { LocalStorageKeys } from "@/const/keys";
import { ReactNode } from "react";
import { create } from "zustand";

type State = {
  notifications: NotificationInfo[];
};

type NotificationInfo = {
  id: string;
  title: string;
  message: string | ReactNode;
  createdAt: Date;
  readed: boolean;
  type: NotificationType;
};

type NotificationType = "info" | "warning" | "error" | "success";

type Action = {
  clearNotifications: () => void;
  addNotification: (notification: Pick<NotificationInfo, "title" | "message" | "type">) => void;
  markNotificationAsRead: (id: string) => void;
  saveNotificationsToLocalStorage: () => void;
  removeNotification: (id: string) => void;
};

const notificationStore = create<State & Action>()((set, get) => ({
  notifications: [],

  clearNotifications: () => {
    const { saveNotificationsToLocalStorage } = get();
    set({ notifications: [] });
    saveNotificationsToLocalStorage();
  },

  saveNotificationsToLocalStorage: () => {
    localStorage.setItem(LocalStorageKeys.NOTIFICATIONS, JSON.stringify(get().notifications));
  },

  addNotification: (notification: Pick<NotificationInfo, "title" | "message" | "type">) => {
    const { saveNotificationsToLocalStorage } = get();

    const newNotification: NotificationInfo = {
      id: crypto.randomUUID(),
      title: notification.title,
      message: notification.message,
      createdAt: new Date(),
      readed: false,
      type: notification.type
    };

    const newNotifications = [...get().notifications, newNotification];
    set({ notifications: newNotifications });
    saveNotificationsToLocalStorage();
  },

  markNotificationAsRead: (id: string) => {
    const { saveNotificationsToLocalStorage } = get();

    set({
      notifications: get().notifications.map((notification) =>
        notification.id === id ? { ...notification, readed: true } : notification
      )
    });
    saveNotificationsToLocalStorage();
  },

  removeNotification: (id: string) => {
    const { saveNotificationsToLocalStorage } = get();
    set({ notifications: get().notifications.filter((notification) => notification.id !== id) });
    saveNotificationsToLocalStorage();
  }
}));

export default notificationStore;
